/**
 * Collection page behaviour (sections/collection-filters.liquid, sections/main-collection.liquid):
 * - <collection-view>: the [ ARCHIVE GRID ] / [ DECK MODE ] toggle. The view lives on
 *   <html data-collection-view>, so collection.css shows one panel; the choice is kept in
 *   localStorage. The inline script in collection-filters applies a saved view before paint.
 * - <collection-grid>: below 990px each band is a scroll-snap strip; this adds the mono 1/3
 *   counter in the band corner and keeps it in step with the strip.
 */
import { defineElement, prefersReducedMotion } from '@wispaire/global';

export const VIEW_KEY = 'wispaire:collection-view';
const VIEWS = ['grid', 'deck'];

/** The saved view, or 'grid' when there is none, it is unknown, or storage is unavailable. */
export function readView(storage) {
  try {
    const view = storage?.getItem(VIEW_KEY);
    return VIEWS.includes(view) ? view : 'grid';
  } catch {
    return 'grid';
  }
}

/** Saves the view; false when storage refuses (private mode, quota). */
export function writeView(storage, view) {
  try {
    storage.setItem(VIEW_KEY, view);
    return true;
  } catch {
    return false;
  }
}

/**
 * The band corner counter for a strip: the lead card's position, and the last once the strip
 * reaches its end. null when the band fits without scrolling.
 */
export function bandCounter({ scrollLeft, scrollWidth, clientWidth, cellWidth, count }) {
  const end = scrollWidth - clientWidth;
  if (end <= 1) return null;
  const position = scrollLeft >= end - 1 ? count : Math.min(count, Math.round(scrollLeft / cellWidth) + 1);
  return `${position}/${count}`;
}

const Base = globalThis.HTMLElement ?? class {};
/** localStorage, or undefined where even reading the property throws (blocked storage). */
const storage = () => {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
};

export class CollectionView extends Base {
  connectedCallback() {
    this.buttons = [...this.querySelectorAll('[data-view]')];
    this.sync(readView(storage()));
    this.addEventListener('click', (event) => {
      const view = event.target.closest('[data-view]')?.dataset.view;
      if (!view) return;
      writeView(storage(), view);
      // The grid and the deck cross-fade as one change; reduced motion swaps them at once.
      if (document.startViewTransition && !prefersReducedMotion()) document.startViewTransition(() => this.sync(view));
      else this.sync(view);
    });
  }

  sync(view) {
    document.documentElement.dataset.collectionView = view;
    for (const button of this.buttons) button.setAttribute('aria-pressed', String(button.dataset.view === view));
  }
}

export class CollectionGrid extends Base {
  connectedCallback() {
    this.counters = [...this.querySelectorAll('.band')].flatMap((band) => {
      const strip = band.querySelector('.band__cards');
      if (!strip || strip.children.length < 2) return [];
      const counter = document.createElement('span');
      counter.className = 'band__counter mono';
      counter.setAttribute('aria-hidden', 'true');
      band.append(counter);
      const update = () => {
        const text = bandCounter({
          scrollLeft: Math.abs(strip.scrollLeft),
          scrollWidth: strip.scrollWidth,
          clientWidth: strip.clientWidth,
          cellWidth: strip.firstElementChild.offsetWidth || 1,
          count: strip.children.length,
        });
        counter.hidden = !text;
        if (text) counter.textContent = text;
      };
      let frame = 0;
      const schedule = () => {
        frame ||= requestAnimationFrame(() => {
          frame = 0;
          update();
        });
      };
      strip.addEventListener('scroll', schedule, { passive: true });
      return [update];
    });
    this.resize = new ResizeObserver(() => this.counters.forEach((update) => update()));
    this.resize.observe(this);
  }

  disconnectedCallback() {
    this.resize?.disconnect();
  }
}

defineElement('collection-view', CollectionView);
defineElement('collection-grid', CollectionGrid);
